/**
 * Page-side half of the host generation changeover (见 18 版本换代).
 *
 * The host retires when a page from another build connects, and every page then
 * reloads — that is what converges the whole origin onto one generation. One
 * decision has to be made before reloading, and it is the reason this module
 * exists: **has this page already reloaded for this very pair of builds?** If it
 * has, reloading again would do exactly as much good as the first time, which is
 * how a mid-rollout origin that keeps serving inconsistent files turns a
 * self-healing mechanism into an endless refresh loop.
 *
 * Kept free of DOM types (a narrow storage interface, no `window`) so the rule is
 * unit-testable — same shape and same reason as `connection-lock.ts`.
 */

/** The slice of `sessionStorage` this needs. Per-tab, which is the right scope. */
export interface GenerationStoreLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * `reload` = pick up the new build. `stuck` = we already tried that and came back
 * to the same disagreement, so tell the user instead of looping.
 */
export type GenerationAction = 'reload' | 'stuck';

/** Where the last changeover attempt is recorded. */
const RELOAD_MARKER_KEY = 'hibro:generation-reload';

/**
 * How long a recorded attempt suppresses the next one.
 *
 * Long enough to cover a reload plus the handshake that follows it, short enough
 * that a genuinely new deploy an hour later is not mistaken for a loop.
 */
export const RELOAD_BREAKER_WINDOW_MS = 60_000;

interface ReloadMarker {
  pageBuildId: string;
  hostBuildId: string;
  at: number;
}

/**
 * Decide what to do about a `hibro:generation-changed`, recording the attempt.
 *
 * The pair of build ids is the identity of the disagreement: after a successful
 * reload this page carries a *different* `pageBuildId`, so a later changeover is
 * never mistaken for a repeat. Only coming back with the same pair — the page did
 * not actually change — trips the breaker.
 *
 * A store that throws (private-mode quirks, storage disabled) resolves to
 * `reload`. That is a deliberate lean: the loop it stops requires a persistently
 * inconsistent origin *and* unusable storage, while refusing to reload would
 * strand the tab on a host from another generation — which is the split-brain
 * this whole mechanism exists to prevent.
 */
export function decideGenerationAction(input: {
  pageBuildId: string;
  hostBuildId: string;
  store: GenerationStoreLike | undefined;
  now: number;
}): GenerationAction {
  const { pageBuildId, hostBuildId, store, now } = input;
  const previous = readMarker(store);
  if (
    previous &&
    previous.pageBuildId === pageBuildId &&
    previous.hostBuildId === hostBuildId &&
    now - previous.at < RELOAD_BREAKER_WINDOW_MS
  ) {
    return 'stuck';
  }
  writeMarker(store, { pageBuildId, hostBuildId, at: now });
  return 'reload';
}

function readMarker(
  store: GenerationStoreLike | undefined,
): ReloadMarker | undefined {
  try {
    const raw = store?.getItem(RELOAD_MARKER_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as Partial<ReloadMarker> | null;
    if (
      typeof parsed?.pageBuildId !== 'string' ||
      typeof parsed.hostBuildId !== 'string' ||
      typeof parsed.at !== 'number'
    ) {
      return undefined;
    }
    return {
      pageBuildId: parsed.pageBuildId,
      hostBuildId: parsed.hostBuildId,
      at: parsed.at,
    };
  } catch {
    return undefined;
  }
}

function writeMarker(
  store: GenerationStoreLike | undefined,
  marker: ReloadMarker,
): void {
  try {
    store?.setItem(RELOAD_MARKER_KEY, JSON.stringify(marker));
  } catch {
    // Unwritable storage means the breaker cannot arm; see the note above on why
    // that resolves toward reloading rather than toward refusing to.
  }
}
